import { FC, useEffect, useState } from 'react'

import { useWallet } from '@aptos-labs/wallet-adapter-react'
import { useDisclosure } from '@mantine/hooks'
import { AIRDROP } from 'config'

import { ButtonConnectWallet } from 'components/ButtonConnectWallet'
import { AirdropFinished, Claim } from 'components/Claim'
import { ErrorBoundary } from 'components/ErrorBoundary'
import InvalidNetworkModal from 'components/InvalidNetworkModal'
import { WalletList } from 'components/WalletList'
import { cx } from 'utils/cx'
import { getDateWithOffset } from 'utils/getDateWithOffset.ts'
import { getTimeClaim } from 'utils/getTimeClaim.ts'

import { useCommonConnectStore } from '../WalletProvider/store.ts'

import s from './s.module.scss'

const ClaimWidget: FC = () => {
  const [opened, { close, open }] = useDisclosure(false)
  const { account, isLoading } = useWallet()
  const [airdropStatus, setAirdropStatus] = useState(AIRDROP.NOT_STARTED)
  const setShouldUpdateClaim = useCommonConnectStore((state) => state.setShouldUpdateTimestamp)
  const claimWidgetClasses = useCommonConnectStore((state) => state.claimWidgetClasses)

  const [currentTime, setCurrentTime] = useState(new Date())

  const [claimTime, setClaimTime] = useState<{
    end: Date
    start: Date
  } | null>(null)

  const updateAirdropStatus = ({ end, now, start }: { end: Date; now: Date; start: Date }) => {
    const _now = now.getTime()
    const _start = start.getTime()
    const _end = end.getTime()

    if (_now < _start) {
      setAirdropStatus(AIRDROP.NOT_STARTED)
    } else if (_now > _end) {
      setAirdropStatus(AIRDROP.HAS_FINISHED)
    } else {
      setAirdropStatus(AIRDROP.HAS_STARTED)
    }
  }

  const onClaim = () => {
    setShouldUpdateClaim(Date.now())
  }

  useEffect(() => {
    getTimeClaim().then(({ end, start }) => {
      const _end = new Date(end)
      const _start = new Date(start)
      setClaimTime({
        end: _end,
        start: _start,
      })
    })
  }, [])

  useEffect(() => {
    if (!claimTime?.end || !claimTime?.start) return
    const _now = getDateWithOffset(new Date())
    updateAirdropStatus({ end: claimTime?.end, now: _now, start: claimTime?.start })
  }, [currentTime])

  useEffect(() => {
    const interval = setInterval(() => {
      const _now = getDateWithOffset(new Date())
      setCurrentTime(_now)
    }, 1000)
    return () => {
      clearInterval(interval)
    }
  }, [])

  const isAirdropFinished = airdropStatus === AIRDROP.HAS_FINISHED

  return (
    <>
      {!account && (
        <div className={s.wrapper}>
          {isAirdropFinished ? (
            <AirdropFinished />
          ) : (
            <>
              <ButtonConnectWallet
                className={cx(claimWidgetClasses.connectWalletButton, s.buttonClaimConnectWallet)}
                disabled={isLoading}
                onClick={open}
                title={isLoading ? 'Loading...' : 'Connect to check your eligibility'}
              />
              <span>Connect an Aptos wallet to see if you&apos;re eligible for an LSD allocation</span>
            </>
          )}
        </div>
      )}
      <ErrorBoundary>
        {account && <Claim account={account} airdropStatus={airdropStatus} onClaim={onClaim} />}
      </ErrorBoundary>
      <ErrorBoundary>
        <WalletList centered onClose={close} opened={opened} />
      </ErrorBoundary>
      <InvalidNetworkModal />
    </>
  )
}

export default ClaimWidget
